import { FC, FormEvent, useEffect, useRef, useState } from 'react'
import { SearchInputField } from '@components-v2/atoms/SearchInputField'
import { SelectDropdown } from '@components-v2/molecules/SelectDropdown'
import styles from './index.module.scss'

type SelectableItem = {
  instance: {
    id: string
    name: string
  }

  isSelected: boolean
}

type Props = {
  placeholder?: string
  name: string
  testid?: string
  onInputFinished: (searchWord: string) => void
  inputWaitingTime?: number
  selectedItems?: { id: string; name: string }[]
  selectableItems?: SelectableItem[]
  onSelect?: (id: string) => void
  onUnselect?: (id: string) => void
  onUnselectAll?: () => void
}

export const SearchInputSelectFieldGroup: FC<Props> = ({
  placeholder = '',
  name,
  testid,
  onInputFinished,
  inputWaitingTime = 200,
  selectedItems,
  selectableItems,
  onSelect = () => {},
  onUnselect = () => {},
  onUnselectAll = () => {},
}) => {
  const ref = useRef<HTMLDivElement>()
  const [value, setValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const close = () => setIsOpen(false)
  const open = () => setIsOpen(true)

  let timer
  const onChange = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.value)

    // setTimeout の前にドロップダウンを開く
    if (!isOpen) {
      open()
    }

    if (timer) {
      clearTimeout(timer)
    }

    // inputWaitingTime ms に一回だけ検索メソッドを呼ぶために入力完了を待つ
    const trimmedValue = e.currentTarget.value.trim() // 前後の空白類を削除
    timer = setTimeout(() => onInputFinished(trimmedValue), inputWaitingTime)
  }

  const onFocus = (e: FormEvent<HTMLInputElement>) => {
    const trimmedValue = e.currentTarget.value.trim() // 前後の空白類を削除
    onInputFinished(trimmedValue)

    // value が空の場合、「一瞬該当の...が見つかりません」と表示されるので一定時間待つ
    if (!isOpen) {
      setTimeout(() => open(), inputWaitingTime)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!ref.current) {
        return
      }

      if (ref.current.contains(event.target as Node)) {
        return
      }

      if (!event.target) {
        return
      }

      close()
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [])

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.input}>
        <SearchInputField
          placeholder={placeholder}
          name={name}
          testid={testid}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          focused={isOpen}
          selectedItems={selectedItems}
          onDeleteSelectedItem={onUnselect}
          onDeletedAllSelectedItems={onUnselectAll}
        />
      </div>
      {isOpen && (
        <div className={styles.dropdown}>
          <SelectDropdown items={selectableItems} onSelect={onSelect} onUnselect={onUnselect} />
        </div>
      )}
    </div>
  )
}
